//Dependencies............................................
import React, {useEffect, useState} from "react";
import axios from 'axios';
import {Input, Modal, Form, Select, Checkbox, Row, Col, Space, Typography} from 'antd';
import { CarOutlined } from '@ant-design/icons';
import {
    useQuery,
    useQueryClient,
    useMutation,
} from '@tanstack/react-query'

//Components..............................................
import {BASE_URL} from '../../../utils/constants';
import {VehicleItem} from "../../../utils/interfaces";
import { NotificationInstance } from "antd/es/notification/interface";

interface Props {
  showModal: boolean,
  onClose: () => void
  api: NotificationInstance
}

interface VehicleType {
  vehicle_manufacturer: string,
  vehicle_model: string
}

type CarMakerListResponse = {
  success: boolean,
  content: string[]
}

const AddVehicleModal: React.FC<Props> = ({showModal, onClose, api}: Props) => {

  const [form] = Form.useForm<VehicleItem>();

  const [manualManufacturer, setManualManufacturer] = useState(false);
  const [manualModel, setManualModel] = useState(false);
  const [submittable, setSubmittable] = useState(false);

  // Re-validate on every keystroke/selection so the Add button only enables
  // once all required fields actually pass validation - not just once they
  // have some value in them.
  const watchedValues = Form.useWatch([], form);
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, watchedValues]);

  const carMakerListQuery = useQuery({
    queryKey: ['carMakerList'],
    queryFn: async () => {
      const res = await axios.get<CarMakerListResponse>(`${BASE_URL}/vehicle_types_manufacturer`)
      return res.data.content.map((value: any) => ({
        value: value,
        label: value
      }));
    }
  })

  const carModelListQuery = useQuery({
    queryKey: ['carModelList'],
    queryFn: async () => {
      const res = await axios.get<CarMakerListResponse>(`${BASE_URL}/vehicle_models_for_manufacturer/${form.getFieldValue("manufacturer")}`)
      return res.data.content.map((value: any) => ({
        value: value,
        label: value
      }));
    },
    enabled: false
  })

  const queryClient = useQueryClient()

  const addVehicleTypeMutation = useMutation({
    mutationFn: (values: VehicleType) => axios.post(`${BASE_URL}/add_vehicle_type`, values),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['carMakerList', 'carModelList']}),
  })

  const addVehicleMutation = useMutation({
    mutationFn: (values: VehicleItem) => axios.post(`${BASE_URL}/generate_vehicle`, values),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['vehicleList']}),
  })

  const submitting = addVehicleMutation.isPending || addVehicleTypeMutation.isPending;

  const onFinish = (vehicle: VehicleItem) => {
    console.log('Received values of form: ', vehicle);
    if (manualManufacturer || manualModel) {
      addVehicleTypeMutation.mutate({
        vehicle_manufacturer: vehicle.manufacturer!,
        vehicle_model: vehicle.model!
      })
    }

    addVehicleMutation.mutate(vehicle, {
      onSuccess: () => {
        onClose();
        api.success({
          title: "Success",
          description: "Vehicle added successfully",
          placement: "bottomRight",
          showProgress: true
        });
      },
      onError: (error) => {
        console.error("Error adding vehicle:", error);
        api.error({
          title: "Error Adding Vehicle",
          description: `There was an error adding the vehicle: ${error.message}`,
          placement: "bottomRight",
          showProgress: true
        });
      }
    });
  };

  const getVehicleManufacturerNode = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <span>Vehicle Manufacturer</span>
        <Checkbox
          disabled={submitting}
          checked={manualManufacturer}
          onChange={() => {
            const value = !manualManufacturer
            setManualManufacturer(value)
            setManualModel(value)
          }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Enter manually</Typography.Text>
        </Checkbox>
      </div>
    )
  }

  const getVehicleModelNode = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <span>Vehicle Model</span>
        <Checkbox
          disabled={submitting || manualManufacturer}
          checked={manualModel}
          onChange={() => setManualModel(!manualModel)}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Enter manually</Typography.Text>
        </Checkbox>
      </div>
    )
  }

  const generateYearsArray = (startYear: number) => {
    const currentYear = new Date().getFullYear();
    const yearsArray = [];
    for (let year = startYear; year <= currentYear; year++) {
      yearsArray.push({label: year.toString(), value: year.toString()});
    }
    return yearsArray;
  };

  const yearStart = Form.useWatch('year_start', form);
  const yearEnd = Form.useWatch('year_end', form);

  return (
    <Modal
      title={
        <Space>
          <CarOutlined />
          Add New Vehicle
        </Space>
      }
      open={showModal}
      width={560}
      okText="Add"
      cancelText="Cancel"
      okButtonProps={{
          autoFocus: false,
          htmlType: 'submit',
          loading: submitting,
          disabled: !submittable || submitting,
      }}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      mask={{ closable: !submitting }}
      onCancel={onClose}
      modalRender={(dom) => (
        <Form
          layout="vertical"
          form={form}
          name="form_add_vehicle"
          clearOnDestroy
          onFinish={(values) => onFinish(values)}
        >
          {dom}
        </Form>
      )}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={getVehicleManufacturerNode()}
              name="manufacturer"
              rules={[{required: true, message: 'Manufacturer is required'}]}
            >
              {!manualManufacturer ? (
                <Select
                  showSearch
                  disabled={submitting}
                  placeholder="Select manufacturer"
                  filterOption={(input, option) => String(option?.label ?? '').toLowerCase().startsWith(input)}
                  loading={carMakerListQuery.isLoading}
                  options={carMakerListQuery.data}
                  onChange={() => {
                    form.setFieldValue('model', undefined)
                    carModelListQuery.refetch()
                  }}
                />
              ) : (
                <Input disabled={submitting} placeholder="e.g. Toyota"/>
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={getVehicleModelNode()}
              name="model"
              rules={[{required: true, message: 'Model is required'}]}
            >
              {!manualModel ? (
                <Select
                  showSearch
                  disabled={submitting}
                  placeholder="Select model"
                  filterOption={(input, option) => String(option?.label ?? '').toLowerCase().startsWith(input)}
                  loading={carModelListQuery.isLoading}
                  options={carModelListQuery.data}
                />
              ) : (
                <Input disabled={submitting} placeholder="e.g. Hilux"/>
              )}
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Type"
          name="type"
          extra="Optional - e.g. Pickup, SUV, Van"
          rules={[{required: false}]}
        >
          <Input disabled={submitting} placeholder="e.g. Pickup"/>
        </Form.Item>
        <Form.Item label="Year Range" required>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              name="year_start"
              rules={[{ required: true, message: 'Select start year' }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Select
                placeholder="From"
                disabled={submitting}
                options={generateYearsArray(1986)}
                onChange={(value) => {
                  if (!yearEnd || value > yearEnd) {
                    form.setFieldValue('year_end', value);
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              name="year_end"
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Select
                placeholder="To"
                disabled={submitting || !yearStart}
                options={generateYearsArray(1986).filter(
                  (y) => !yearStart || y.value >= yearStart
                )}
              />
            </Form.Item>
          </div>
        </Form.Item>
    </Modal>
  )
}
export default AddVehicleModal;

